import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, CheckCircle, TrendingUp, BarChart2, Sparkles, RefreshCw, Flag } from 'lucide-react';
import { useInstanceAnalytics, useInstanceResponses, useDeleteResponse, useRegenerateSummary, useInstanceTrends } from '../hooks/useAnalyticsApi';
import { useFeedbackInstance } from '../hooks/useFeedbackInstanceApi';
import PieChartWidget from '../components/charts/PieChartWidget';
import BarChartWidget from '../components/charts/BarChartWidget';
import WordCloudWidget from '../components/charts/WordCloudWidget';
import MetricCard from '../components/charts/MetricCard';
import ResponsesList from '../components/analytics/ResponsesList';
import GroupedResponsesList from '../components/analytics/GroupedResponsesList';
import ResponseDetailModal from '../components/analytics/ResponseDetailModal';
import LineChartWidget from '../components/charts/LineChartWidget';
import TrendSummaryCards from '../components/analytics/TrendSummaryCards';
import OccurrenceFilterDropdown from '../components/analytics/OccurrenceFilterDropdown';
import { ResponseWithAnswersDto } from '../services/feedbackService';

type TabType = 'charts' | 'trends' | 'responses';

const InstanceAnalytics: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('charts');
  const [selectedResponse, setSelectedResponse] = useState<ResponseWithAnswersDto | null>(null);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | undefined>(undefined);

  const { data: instance, isLoading: loadingInstance } = useFeedbackInstance(instanceId || '');
  const isRecurring = instance?.frequency !== 'once';
  const { data: trends, isLoading: loadingTrends } = useInstanceTrends(instanceId, isRecurring);
  const { data: analytics, isLoading: loadingAnalytics } = useInstanceAnalytics(instanceId, selectedOccurrenceId);
  const { data: responses, isLoading: loadingResponses, isError: responsesError } = useInstanceResponses(instanceId);
  const deleteResponse = useDeleteResponse(instanceId);
  const regenerateSummary = useRegenerateSummary(instanceId);

  const selectedIndex = useMemo(() => {
    if (!selectedResponse || !responses) return -1;
    return responses.findIndex((r) => r.id === selectedResponse.id);
  }, [selectedResponse, responses]);

  const handlePrevious = () => {
    if (!responses || selectedIndex <= 0) return;
    setSelectedResponse(responses[selectedIndex - 1]);
  };

  const handleNext = () => {
    if (!responses || selectedIndex >= responses.length - 1) return;
    setSelectedResponse(responses[selectedIndex + 1]);
  };

  if (loadingInstance || loadingAnalytics) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            ))}
          </div>
          <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!instance || !analytics) {
    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => navigate('/instances')}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Instances
        </button>
        <div className="text-center py-12">
          <BarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">Analytics not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/instances')}
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Instances
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Feedback Results
          </h1>

        </div>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            instance.status === 'active'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : instance.status === 'completed'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {instance.status}
        </span>
      </div>

      <div className={`grid grid-cols-1 ${analytics.totalFlaggedQuestions > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6`}>
        <MetricCard
          title="Assigned"
          value={analytics.totalAssignees}
          description="Total assignees"
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Completed"
          value={`${analytics.completedResponses} / ${analytics.totalAssignees}`}
          description="Submitted responses"
          icon={CheckCircle}
          color="green"
        />
        <MetricCard
          title="Completion Rate"
          value={`${analytics.completionRate}%`}
          description="Response rate"
          icon={TrendingUp}
          color="purple"
        />
        {analytics.totalFlaggedQuestions > 0 && (
          <MetricCard
            title="Flags Triggered"
            value={analytics.totalFlagsTriggered}
            description={`Across ${analytics.totalFlaggedQuestions} flagged question(s)`}
            icon={Flag}
            color="red"
          />
        )}
      </div>

      {analytics.aiSummary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Summary</h2>
            <button
              onClick={() => regenerateSummary.mutate()}
              disabled={regenerateSummary.isPending}
              title="Regenerate summary"
              className="ml-auto p-1.5 rounded-md text-gray-400 hover:text-purple-600 hover:bg-purple-100 dark:hover:text-purple-400 dark:hover:bg-purple-900/30 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${regenerateSummary.isPending ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-line">
            {analytics.aiSummary.split(/(\*\*.+?\*\*)/).map((part, index) => {
              const boldMatch = part.match(/^\*\*(.+)\*\*$/);
              if (boldMatch) return <strong key={index} className="text-gray-900 dark:text-white">{boldMatch[1]}</strong>;
              return <span key={index}>{part}</span>;
            })}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('charts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'charts'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Charts
            </button>
            {isRecurring && (
              <button
                onClick={() => setActiveTab('trends')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'trends'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Trends
              </button>
            )}
            <button
              onClick={() => setActiveTab('responses')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'responses'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Responses ({responses?.length || 0})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'charts' && (
            <div className="space-y-8">
              {isRecurring && trends?.occurrenceSummaries && trends.occurrenceSummaries.length > 0 && (
                <OccurrenceFilterDropdown
                  occurrences={trends.occurrenceSummaries}
                  selectedOccurrenceId={selectedOccurrenceId}
                  onChange={setSelectedOccurrenceId}
                />
              )}
              {analytics.questionAnalytics.length === 0 ? (
                <div className="text-center py-12">
                  <BarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">
                    No chart data available yet
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    Charts will appear when responses are collected
                  </p>
                </div>
              ) : (
                <>
                {analytics.questionAnalytics.map((question) => (
                  <div
                    key={question.questionId}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {question.questionText}
                        </h3>
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 capitalize">
                          {question.questionType.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        {question.hasFlag && (
                          <span className="inline-flex items-center gap-1 mt-1 ml-2 px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                            🚩 {question.flagLabel}: {question.flagTriggeredCount}/{question.flagTotalAnswers}
                          </span>
                        )}
                      </div>
                    </div>
                    {question.chartType === 'text' ? (
                      <>
                        <ul className="space-y-2">
                          {question.textResponses?.map((response, index) => (
                            <li
                              key={index}
                              className="px-4 py-3 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600"
                            >
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {response.respondentName}
                              </p>
                              <p className="text-gray-700 dark:text-gray-300 text-sm">
                                {response.text}
                              </p>
                            </li>
                          ))}
                        </ul>
                        {question.textResponses && question.textResponses.length > 0 && (
                          <WordCloudWidget responses={question.textResponses} />
                        )}
                      </>
                    ) : question.chartType === 'pie' ? (
                      <PieChartWidget data={question.chartData} />
                    ) : (
                      <BarChartWidget data={question.chartData} />
                    )}
                  </div>
                ))}
              </>
              )}
            </div>
          )}

          {activeTab === 'trends' && isRecurring && (
            loadingTrends ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">Loading trends...</p>
              </div>
            ) : !trends || trends.questionTrends.length === 0 ? (
              <div className="text-center py-12">
                <BarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">No trend data available yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                <TrendSummaryCards summaries={trends.occurrenceSummaries} />
                {trends.questionTrends.map((trend) => {
                  const distributionKeys = Array.from(
                    new Set(
                      trend.dataPoints.flatMap((point) =>
                        point.distribution ? Object.keys(point.distribution) : []
                      )
                    )
                  );

                  const chartData = trend.dataPoints.map((point) => ({
                    label: point.label,
                    averageValue: point.averageValue ?? 0,
                    responseCount: point.responseCount,
                    ...distributionKeys.reduce<Record<string, number>>((acc, key) => {
                      acc[key] = point.distribution?.[key] ?? 0;
                      return acc;
                    }, {}),
                  }));

                  const lines =
                    trend.trendType === 'distribution'
                      ? distributionKeys.map((key, index) => ({
                          dataKey: key,
                          name: key,
                          color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'][index % 8],
                        }))
                      : [
                          {
                            dataKey: trend.trendType === 'text_count' ? 'responseCount' : 'averageValue',
                            name: trend.trendType === 'text_count' ? 'Responses' : 'Average',
                            color: '#3B82F6',
                          },
                        ];

                  return (
                    <div
                      key={trend.questionId}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6"
                    >
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {trend.questionText}
                        </h3>
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 capitalize">
                          {trend.questionType.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                      <LineChartWidget
                        data={chartData}
                        lines={lines}
                        yAxisLabel={trend.trendType === 'text_count' ? 'Responses' : trend.trendType === 'average' ? 'Average' : undefined}
                      />
                    </div>
                  );
                })}
              </div>
            )
          )}

          {activeTab === 'responses' && (
            loadingResponses ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">Loading responses...</p>
              </div>
            ) : responsesError ? (
              <div className="text-center py-12">
                <p className="text-red-600 dark:text-red-400 font-medium">Unable to load responses</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  You may not have permission to view individual responses.
                </p>
              </div>
            ) : isRecurring ? (
              <GroupedResponsesList
                responses={responses || []}
                occurrenceSummaries={trends?.occurrenceSummaries || []}
                onResponseClick={setSelectedResponse}
                onDeleteResponse={(responseId) => deleteResponse.mutate(responseId)}
                isDeleting={deleteResponse.isPending}
              />
            ) : (
              <ResponsesList
                responses={responses || []}
                onResponseClick={setSelectedResponse}
                onDeleteResponse={(responseId) => deleteResponse.mutate(responseId)}
                isDeleting={deleteResponse.isPending}
              />
            )
          )}
        </div>
      </div>

      {selectedResponse && (
        <ResponseDetailModal
          response={selectedResponse}
          onClose={() => setSelectedResponse(null)}
          onPrevious={selectedIndex > 0 ? handlePrevious : undefined}
          onNext={selectedIndex < (responses?.length || 0) - 1 ? handleNext : undefined}
          currentIndex={selectedIndex}
          totalCount={responses?.length || 0}
        />
      )}
    </div>
  );
};

export default InstanceAnalytics;
